import { useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import { axiosInstanceNew } from '../utils/axios';

// Only one session-expiry interceptor may be active at a time. If this hook is
// mounted in more than one place, the extra mounts are no-ops instead of
// logging the user out (and toasting) twice.
let activeOwner = null;

const SESSION_TOAST_ID = 'session-expired';

/**
 * Logs the user out when the server reports an expired session (401).
 *
 * @param {Function} logout          AuthProvider.logout
 * @param {boolean}  initialLoad     true while the session bootstrap runs
 * @param {boolean}  isAuthenticated true when ANY identity (user/admin/lp) is signed in
 */
export function useAxiosAuthInterceptor(logout, initialLoad, isAuthenticated = true) {
  const logoutRef = useRef(logout);
  const initialLoadRef = useRef(initialLoad);
  const isAuthenticatedRef = useRef(isAuthenticated);

  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  useEffect(() => {
    initialLoadRef.current = initialLoad;
  }, [initialLoad]);

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    const owner = {};

    if (activeOwner) return undefined;
    activeOwner = owner;

    let lastHandledAt = 0;

    const interceptorId = axiosInstanceNew.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const message = String(error?.response?.data?.message || '');
        const requestConfig = error?.config || {};
        const url = String(requestConfig.url || '');

        // 1) never for login / auth bootstrap endpoints
        if (url.includes('/login') || url.includes('/auth/me') || url.includes('/auth/logout')) {
          return Promise.reject(error);
        }

        // 2) per-request opt-out
        if (requestConfig.skipAuthInterceptor) return Promise.reject(error);

        // 3) only real session-expired 401s, not invalid credentials etc.
        if (status !== 401 || !message.toLowerCase().includes('unauthorized')) {
          return Promise.reject(error);
        }

        // 4) never during the initial load, and never for guests
        //    (a guest hitting a protected endpoint has no session to expire)
        if (initialLoadRef.current) return Promise.reject(error);
        if (!isAuthenticatedRef.current) return Promise.reject(error);

        const now = Date.now();

        if (now - lastHandledAt > 5000) {
          lastHandledAt = now;
          logoutRef.current?.();
          toast.error('Session expired. Please log in again.', { id: SESSION_TOAST_ID });
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axiosInstanceNew.interceptors.response.eject(interceptorId);

      if (activeOwner === owner) {
        activeOwner = null;
      }
    };
  }, []);
}
